import type { ReportEntryCategoryV2, ReportEntryIdentification } from '@/models/ReportEntryV2'
import type { IdentityRejection } from '@/models/registry'

/**
 * MYAC-1160 review pass (NOW-1) — the ONE rule for "this entry renders the
 * « Votre revue » control", read by both the card (`ReportEntryCardV2`'s
 * `isReviewable`) and the counter (`regulatoryMonitoring`'s
 * `reviewProgressV2`). They used to state it twice and disagree: the counter
 * said "identified, not upcoming, not unchanged", which also swept in entries
 * whose category is `unknown` — a value `ReportViewV2`'s `visibleSections`
 * has no section for (`REPORT_SECTION_ORDER` in `regulationReportSeverity.ts`
 * lists ten known keys, none of them `unknown`), so those entries render NO
 * card at all. On the 2026-08-11 fixture that was 116 as a denominator for
 * 114 reachable cards: a counter no one could ever finish.
 */
export interface ReviewableEntryFactsV2 {
  identification: ReportEntryIdentification
  category: ReportEntryCategoryV2
  family_rejection: IdentityRejection | null
}

/** The categories with a section of their own that carries review controls.
 *  `upcoming` and `unchanged` render sections but no controls (nothing has
 *  happened yet / nothing changed); `unknown` renders no section at all. */
export const REVIEWABLE_CATEGORIES_V2: readonly ReportEntryCategoryV2[] = [
  'withdrawn',
  'published',
  'adopted',
  'unrecognised',
]

export function isReviewableEntryV2(entry: ReviewableEntryFactsV2): boolean {
  if (entry.identification !== 'identified') return false
  // A terminally rejected family lands in the `rejected` section whatever its
  // stored category (see the store's `rejectedEntriesV2`), and that card does
  // render the control — so it counts. Truthy check, not `!== null`: some
  // callers' fixtures leave `family_rejection` `undefined` rather than
  // `null` for "not rejected".
  if (entry.family_rejection) return true
  return REVIEWABLE_CATEGORIES_V2.includes(entry.category)
}
